import {
  QueryClient,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import produce from 'immer';
import { omit } from 'underscore';

import { deleteStudentScore } from '../../api/studentScore';
import { ActivityResponse } from './useActivity';

export function removeStudentScore({
  activity,
  questionId,
  studentId,
}: {
  activity: Pick<ActivityResponse, 'questionScores'>;
  questionId: number;
  studentId: number;
}) {
  return produce(activity, (nextActivity) => {
    const questionScore = nextActivity.questionScores[questionId];
    if (!questionScore) return;
    questionScore.studentScores = omit(
      questionScore.studentScores,
      `${studentId}`
    );

    if (Object.keys(questionScore.studentScores).length === 0) {
      nextActivity.questionScores = omit(
        nextActivity.questionScores,
        `${questionId}`
      );
    }
  });
}

function updateStudentScoreCache({
  queryClient,
  activityInstanceId,
  questionId,
  studentId,
}: {
  queryClient: QueryClient;
  activityInstanceId: number;
  questionId: number;
  studentId: number;
}) {
  queryClient.setQueryData(
    ['activity', activityInstanceId],
    (activity: ActivityResponse) => {
      return removeStudentScore({ activity, questionId, studentId });
    }
  );
}

export function useDeleteStudentScore({
  activityInstanceId,
  questionId,
}: {
  activityInstanceId: number;
  questionId: number;
}) {
  const queryClient = useQueryClient();

  return useMutation(
    ({
      questionScoreId,
      isRostered,
      studentId,
    }: {
      questionScoreId: number;
      isRostered: boolean;
      studentId: number;
    }) =>
      deleteStudentScore({
        questionScoreId,
        isRostered,
        studentId,
      }),
    {
      onSuccess: (data, { studentId }) => {
        updateStudentScoreCache({
          queryClient: queryClient,
          activityInstanceId: activityInstanceId,
          questionId: questionId,
          studentId: studentId,
        });
      },
    }
  );
}
